import React, { useState, useEffect } from 'react';
import { Users, Target, Clock, AlertTriangle, Edit2, DollarSign, TrendingUp, FileText } from 'lucide-react';
import { KPIService } from '../../services/kpiService';
import CorrectionReasonsEditor from './CorrectionReasonsEditor';
import EditCorrectionModal from './EditCorrectionModal';

/**
 * Manager KPI Dashboard
 * Comprehensive view of all team KPIs:
 * - Overview: Summary of all team metrics
 * - Model Builders: Approval rates for all builders
 * - Data Prep: Response times and throughput
 * - Review: Invoiced amounts, hours per job, and trend
 * - Corrections: Recent corrections log
 */
const ManagerDashboard = ({ startDate, endDate, periodLabel, userEmail }) => {
  const canEditSettings = KPIService.canEditCorrectionReasons(userEmail);
  const isManager = KPIService.isManager(userEmail);
  const [activeTab, setActiveTab] = useState('overview');
  const [modelBuilderKPIs, setModelBuilderKPIs] = useState([]);
  const [dataPrepKPIs, setDataPrepKPIs] = useState(null);
  const [reviewKPIs, setReviewKPIs] = useState(null);
  const [reviewTrend, setReviewTrend] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCorrection, setEditingCorrection] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [builderData, dataPrepData, correctionsData, reviewData, trendData] = await Promise.all([
          KPIService.getAllModelBuilderKPIs(startDate, endDate),
          KPIService.getDataPrepKPIs(startDate, endDate),
          KPIService.getCorrectionsInRange(startDate, endDate),
          KPIService.getMattKPIs(startDate, endDate),
          KPIService.getMattInvoicedTrend(6),
        ]);
        setModelBuilderKPIs(builderData);
        setDataPrepKPIs(dataPrepData);
        setCorrections(correctionsData);
        setReviewKPIs(reviewData);
        setReviewTrend(trendData);
      } catch (err) {
        console.error('Error fetching manager KPIs:', err);
        setError('Failed to load KPI data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  const refreshCorrections = async () => {
    try {
      const correctionsData = await KPIService.getCorrectionsInRange(startDate, endDate);
      setCorrections(correctionsData);
    } catch (err) {
      console.error('Error refreshing corrections:', err);
    }
  };

  if (loading) {
    return <div className="kpi-dashboard-loading">Loading...</div>;
  }

  if (error) {
    return <div className="kpi-dashboard-error">{error}</div>;
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Calculate aggregate stats
  const totalCompleted = modelBuilderKPIs.reduce((sum, b) => sum + b.totalCompleted, 0);
  const totalFirstPass = modelBuilderKPIs.reduce((sum, b) => sum + b.firstPassCount, 0);
  const overallApprovalRate = totalCompleted > 0 ? (totalFirstPass / totalCompleted) * 100 : 0;

  const getApprovalRateColor = (rate) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'danger';
  };

  return (
    <div className="kpi-dashboard">
      {/* Tabs */}
      <div className="kpi-tabs" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 0 }}>
        <button
          className={`kpi-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`kpi-tab ${activeTab === 'model-builders' ? 'active' : ''}`}
          onClick={() => setActiveTab('model-builders')}
        >
          Model Builders
        </button>
        <button
          className={`kpi-tab ${activeTab === 'data-prep' ? 'active' : ''}`}
          onClick={() => setActiveTab('data-prep')}
        >
          Data Prep
        </button>
        <button
          className={`kpi-tab ${activeTab === 'review' ? 'active' : ''}`}
          onClick={() => setActiveTab('review')}
        >
          Review
        </button>
        <button
          className={`kpi-tab ${activeTab === 'corrections' ? 'active' : ''}`}
          onClick={() => setActiveTab('corrections')}
        >
          Corrections Log
        </button>
        </div>
        {/* Settings Editor (Adam & Matt only) */}
        {canEditSettings && (
          <CorrectionReasonsEditor userEmail={userEmail} />
        )}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Summary Cards */}
          <div className="kpi-cards-row">
            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-card-label">Team Approval Rate</span>
                <div className="kpi-card-icon green">
                  <Target size={18} />
                </div>
              </div>
              <div className={`kpi-card-value ${getApprovalRateColor(overallApprovalRate)}`}>
                {overallApprovalRate.toFixed(1)}%
              </div>
              <div className="kpi-card-target">
                {totalFirstPass} of {totalCompleted} first pass
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-card-label">Projects Completed</span>
                <div className="kpi-card-icon blue">
                  <Users size={18} />
                </div>
              </div>
              <div className="kpi-card-value">
                {totalCompleted}
              </div>
              <div className="kpi-card-target">
                By model builders
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-card-label">Discrepancy Check</span>
                <div className="kpi-card-icon purple">
                  <Clock size={18} />
                </div>
              </div>
              <div className={`kpi-card-value ${(dataPrepKPIs?.outsideTarget || 0) === 0 ? 'success' : 'danger'}`}>
                {dataPrepKPIs?.outsideTarget || 0} / {dataPrepKPIs?.totalCompleted || 0}
              </div>
              <div className="kpi-card-target">
                jobs failed
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-card-label">Corrections This Period</span>
                <div className="kpi-card-icon orange">
                  <AlertTriangle size={18} />
                </div>
              </div>
              <div className={`kpi-card-value ${corrections.length > 0 ? 'warning' : ''}`}>
                {corrections.length}
              </div>
              <div className="kpi-card-target">
                Projects sent back
              </div>
            </div>
          </div>

          {/* Model Builders Summary */}
          <div className="kpi-section">
            <div className="kpi-section-header">
              <h2 className="kpi-section-title">Model Builder Performance</h2>
            </div>
            <div className="kpi-section-content">
              <table className="kpi-table">
                <thead>
                  <tr>
                    <th>Builder</th>
                    <th>Completed</th>
                    <th>First Pass</th>
                    <th>Corrections</th>
                    <th>Approval Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {modelBuilderKPIs.map((builder) => (
                    <tr key={builder.employeeId}>
                      <td>{builder.employeeName}</td>
                      <td>{builder.totalCompleted}</td>
                      <td>{builder.firstPassCount}</td>
                      <td>{builder.correctionCount}</td>
                      <td>
                        <span className={`kpi-badge ${getApprovalRateColor(builder.approvalRate)}`}>
                          {builder.approvalRate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Model Builders Tab */}
      {activeTab === 'model-builders' && (
        <div className="kpi-section">
          <div className="kpi-section-header">
            <h2 className="kpi-section-title">Model Builder Details</h2>
          </div>
          <div className="kpi-section-content">
            {modelBuilderKPIs.map((builder) => (
              <div key={builder.employeeId} style={{ marginBottom: 'var(--space-6)' }}>
                <h3 style={{
                  fontSize: 'var(--text-lg)',
                  fontWeight: 600,
                  color: 'var(--gray-100)',
                  marginBottom: 'var(--space-3)'
                }}>
                  {builder.employeeName}
                </h3>
                <div style={{ display: 'flex', gap: 'var(--space-6)', marginBottom: 'var(--space-4)' }}>
                  <div>
                    <span style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>Completed: </span>
                    <span style={{ color: 'var(--gray-100)', fontWeight: 500 }}>{builder.totalCompleted}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>First Pass: </span>
                    <span style={{ color: 'var(--green-400)', fontWeight: 500 }}>{builder.firstPassCount}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>Corrections: </span>
                    <span style={{ color: 'var(--amber-400)', fontWeight: 500 }}>{builder.correctionCount}</span>
                  </div>
                </div>
                <div className="kpi-progress" style={{ marginBottom: 'var(--space-2)' }}>
                  <div
                    className={`kpi-progress-bar ${getApprovalRateColor(builder.approvalRate)}`}
                    style={{ width: `${Math.min(builder.approvalRate, 100)}%` }}
                  />
                </div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)' }}>
                  {builder.approvalRate.toFixed(1)}% approval rate
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Prep Tab */}
      {activeTab === 'data-prep' && (
        <>
          <div className="kpi-cards-row">
            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-card-label">Discrepancy Check</span>
                <div className="kpi-card-icon green">
                  <Clock size={18} />
                </div>
              </div>
              <div className={`kpi-card-value ${(dataPrepKPIs?.outsideTarget || 0) === 0 ? 'success' : 'danger'}`}>
                {dataPrepKPIs?.outsideTarget || 0} / {dataPrepKPIs?.totalCompleted || 0}
              </div>
              <div className="kpi-card-target">
                jobs failed
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-card-label">Projects Processed</span>
                <div className="kpi-card-icon blue">
                  <Users size={18} />
                </div>
              </div>
              <div className="kpi-card-value">
                {dataPrepKPIs?.totalCompleted || 0}
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-card-label">Avg Business Days</span>
                <div className="kpi-card-icon purple">
                  <Clock size={18} />
                </div>
              </div>
              <div className="kpi-card-value">
                {dataPrepKPIs?.avgBusinessDays?.toFixed(1) || '0.0'}
              </div>
            </div>
          </div>

          {dataPrepKPIs?.timers?.length > 0 && (
            <div className="kpi-section">
              <div className="kpi-section-header">
                <h2 className="kpi-section-title">Recent Projects</h2>
              </div>
              <div className="kpi-section-content">
                <table className="kpi-table">
                  <thead>
                    <tr>
                      <th>Job #</th>
                      <th>Project</th>
                      <th>Days</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataPrepKPIs.timers.map((timer) => (
                      <tr key={timer.projectId}>
                        <td>{timer.jobNumber}</td>
                        <td>{timer.projectName}</td>
                        <td>{timer.businessDaysElapsed}</td>
                        <td>
                          <span className={`kpi-badge ${timer.status === 'passed' ? 'success' : 'danger'}`}>
                            {timer.status === 'passed' ? 'Passed' : 'Failed'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Review Tab */}
      {activeTab === 'review' && (
        <>
          <div className="kpi-cards-row">
            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-card-label">Total Invoiced</span>
                <div className="kpi-card-icon green">
                  <DollarSign size={18} />
                </div>
              </div>
              <div className="kpi-card-value">
                {formatCurrency(reviewKPIs?.totalInvoiced || 0)}
              </div>
              <div className="kpi-card-target">
                {reviewKPIs?.projectCount || 0} projects in period
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-card-label">Projects Completed</span>
                <div className="kpi-card-icon blue">
                  <FileText size={18} />
                </div>
              </div>
              <div className="kpi-card-value">
                {reviewKPIs?.projectCount || 0}
              </div>
              <div className="kpi-card-target">
                Invoiced during {periodLabel}
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-card-label">Project Hours</span>
                <div className="kpi-card-icon purple">
                  <Clock size={18} />
                </div>
              </div>
              <div className="kpi-card-value">
                {reviewKPIs?.projectHours?.toFixed(1) || '0.0'}
              </div>
              <div className="kpi-card-target">
                Hours logged to projects
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-card-label">Hours Per Job</span>
                <div className="kpi-card-icon orange">
                  <TrendingUp size={18} />
                </div>
              </div>
              <div className={`kpi-card-value ${reviewKPIs?.isWithinTarget ? 'success' : 'danger'}`}>
                {reviewKPIs?.hoursPerJob?.toFixed(2) || '0.00'}
              </div>
              <div className="kpi-card-target">
                Target: &lt; {reviewKPIs?.target} hrs
                {reviewKPIs?.isWithinTarget ? ' \u2713' : ' \u2717'}
              </div>
            </div>
          </div>

          {/* Invoiced Trend */}
          <div className="kpi-section">
            <div className="kpi-section-header">
              <h2 className="kpi-section-title">Invoiced Trend (Last 6 Months)</h2>
            </div>
            <div className="kpi-section-content">
              <table className="kpi-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Invoiced</th>
                    <th>Projects</th>
                    <th>Hrs/Job</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewTrend.map((period) => (
                    <tr key={period.startDate}>
                      <td>{period.period}</td>
                      <td>{formatCurrency(period.totalInvoiced)}</td>
                      <td>{period.projectCount}</td>
                      <td>{period.hoursPerJob?.toFixed(2) || '-'}</td>
                      <td>
                        {period.projectCount > 0 ? (
                          <span className={`kpi-badge ${period.isWithinTarget ? 'success' : 'danger'}`}>
                            {period.isWithinTarget ? 'On Target' : 'Above Target'}
                          </span>
                        ) : (
                          <span className="kpi-badge warning">No Data</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Projects Invoiced This Period */}
          {reviewKPIs?.projects?.length > 0 && (
            <div className="kpi-section">
              <div className="kpi-section-header">
                <h2 className="kpi-section-title">Projects Invoiced This Period</h2>
              </div>
              <div className="kpi-section-content">
                <table className="kpi-table">
                  <thead>
                    <tr>
                      <th>Job #</th>
                      <th>Project Name</th>
                      <th>Model Price</th>
                      <th>Invoiced Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewKPIs.projects.map((project) => (
                      <tr key={project.id}>
                        <td>{project.jobNumber}-{project.rev || 0}</td>
                        <td>{project.name}</td>
                        <td>{formatCurrency(project.modelPrice)}</td>
                        <td>{project.invoicedDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Corrections Tab */}
      {activeTab === 'corrections' && (
        <div className="kpi-section">
          <div className="kpi-section-header">
            <h2 className="kpi-section-title">Corrections Log</h2>
            {isManager && (
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                Click a row to edit
              </span>
            )}
          </div>
          <div className="kpi-section-content">
            {corrections.length > 0 ? (
              <table className="kpi-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Job #</th>
                    <th>Project</th>
                    <th>Builder</th>
                    <th>Reason</th>
                    <th>Counts</th>
                    {isManager && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {corrections.map((correction) => (
                    <tr
                      key={correction.id}
                      onClick={() => isManager && setEditingCorrection(correction)}
                      style={{ cursor: isManager ? 'pointer' : 'default' }}
                    >
                      <td>{new Date(correction.created_at).toLocaleDateString()}</td>
                      <td>{correction.job_number}</td>
                      <td>{correction.project_name}</td>
                      <td>{correction.modelBuilderName}</td>
                      <td>
                        {correction.selected_reasons?.length > 0
                          ? correction.selected_reasons.join(', ')
                          : correction.reason}
                      </td>
                      <td>
                        <span className={`kpi-badge ${correction.counts_against_builder ? 'warning' : 'success'}`}>
                          {correction.counts_against_builder ? 'Yes' : 'No'}
                        </span>
                      </td>
                      {isManager && (
                        <td>
                          <Edit2 size={14} style={{ color: 'var(--gray-400)' }} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="kpi-empty">
                <AlertTriangle className="kpi-empty-icon" />
                <p className="kpi-empty-text">
                  No corrections logged during this period.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Correction Modal */}
      {editingCorrection && (
        <EditCorrectionModal
          correction={editingCorrection}
          userEmail={userEmail}
          onClose={() => setEditingCorrection(null)}
          onSave={() => {
            setEditingCorrection(null);
            refreshCorrections();
          }}
          onDelete={() => {
            setEditingCorrection(null);
            refreshCorrections();
          }}
        />
      )}
    </div>
  );
};

export default ManagerDashboard;
